/**
 * @file hooks/useWeeklyReview.js
 * @description Surfaces a once-per-week deficit review for the most recently
 * completed week. Tracks which week has been shown in localStorage.
 */

import { useMemo, useState } from "react";
import {
  buildWeeklyDeficits,
  getWeeklyInsights,
  getWeekStart,
} from "../utils/weeklyReview";

const REVIEW_SHOWN_KEY = "weekly_review_shown";

export function useWeeklyReview(meals = [], profileData = {}) {
  const [dismissedKey, setDismissedKey] = useState(() => {
    try {
      return localStorage.getItem(REVIEW_SHOWN_KEY) || "";
    } catch {
      return "";
    }
  });

  const { review, proteinTarget, dailyGoalDeficit } = useMemo(() => {
    const { weeks, proteinTarget, dailyGoalDeficit } = buildWeeklyDeficits(
      meals,
      profileData,
    );

    // Most recent COMPLETED week (i.e. not the current, in-progress week).
    const currentWeekStart = getWeekStart(new Date()).getTime();
    const completed = weeks.filter(
      (w) => w.weekStart.getTime() < currentWeekStart,
    );
    const last = completed[completed.length - 1] || null;

    return { review: last, proteinTarget, dailyGoalDeficit };
  }, [meals, profileData]);

  const isEligible =
    !!review && review.daysLogged >= 2 && review.key !== dismissedKey;

  const insights = useMemo(
    () =>
      review
        ? getWeeklyInsights(review, { proteinTarget, dailyGoalDeficit })
        : null,
    [review, proteinTarget, dailyGoalDeficit],
  );

  function dismissReview() {
    if (review) {
      try {
        localStorage.setItem(REVIEW_SHOWN_KEY, review.key);
      } catch {
        /* ignore */
      }
      setDismissedKey(review.key);
    }
  }

  return {
    reviewOpen: isEligible,
    week: review,
    insights,
    dismissReview,
  };
}

export { REVIEW_SHOWN_KEY };
